/**
 * Dropdown Renderer Widget JavaScript
 *
 * Handles dropdown change events and content visibility switching with fade transitions.
 * Uses Elementor's frontend handler class for proper integration with the editor.
 *
 * @package Hoffmaster
 * @since 1.0.0
 */

/**
 * Dropdown Renderer Handler Class
 *
 * Extends Elementor's base handler to properly integrate with the editor
 * and handle both frontend and preview modes.
 */
class HoffmasterDropdownRendererHandler extends elementorModules.frontend.handlers.Base {
	/**
	 * Get default settings for the handler.
	 *
	 * @return {Object} Default settings with selectors.
	 */
	getDefaultSettings() {
		return {
			selectors: {
				select: '.hm-dropdown-renderer__select',
				contentWrapper: '.hm-dropdown-renderer__content-wrapper',
			},
		};
	}

	/**
	 * Bind events for the widget.
	 *
	 * Called automatically by Elementor when the widget is initialized.
	 *
	 * @return {void}
	 */
	bindEvents() {
		const selectElement = this.$element[0].querySelector(
			this.getSettings('selectors.select')
		);

		if (selectElement) {
			selectElement.addEventListener('change', (e) =>
				this.handleSelectChange(e)
			);
		}
	}

	/**
	 * Handle dropdown select change event.
	 *
	 * Shows the selected content wrapper and hides all others with fade transitions.
	 * Updates ARIA attributes for accessibility.
	 *
	 * @param {Event} event - The change event from the select element.
	 * @return {void}
	 */
	handleSelectChange(event) {
		const selectedSlug = event.target.value;
		const wrappers = this.$element[0].querySelectorAll(
			this.getSettings('selectors.contentWrapper')
		);

		// Update all content wrappers
		wrappers.forEach((wrapper) => {
			const contentId = wrapper.dataset.contentId;

			if (contentId === selectedSlug) {
				// Show selected content
				this.showContent(wrapper);
			} else {
				// Hide other content
				this.hideContent(wrapper);
			}
		});

		// Announce change to screen readers
		this.announceChange(event.target);
	}

	/**
	 * Show content wrapper with fade-in animation.
	 *
	 * @param {HTMLElement} wrapper - The content wrapper to show.
	 * @return {void}
	 */
	showContent(wrapper) {
		// Remove hidden class first to allow display
		wrapper.classList.remove('is-hidden');

		// Force reflow to ensure animation plays
		void wrapper.offsetWidth;

		// Add visible class for animation
		wrapper.classList.add('is-visible');

		// Update ARIA attributes
		wrapper.setAttribute('aria-hidden', 'false');

		// Add aria-live for screen reader announcement
		if (!wrapper.hasAttribute('aria-live')) {
			wrapper.setAttribute('aria-live', 'polite');
		}
	}

	/**
	 * Hide content wrapper.
	 *
	 * @param {HTMLElement} wrapper - The content wrapper to hide.
	 * @return {void}
	 */
	hideContent(wrapper) {
		wrapper.classList.remove('is-visible');
		wrapper.classList.add('is-hidden');
		wrapper.setAttribute('aria-hidden', 'true');

		// Remove aria-live from hidden content
		wrapper.removeAttribute('aria-live');
	}

	/**
	 * Announce content change to screen readers.
	 *
	 * Creates a live region announcement for accessibility.
	 *
	 * @param {HTMLSelectElement} selectElement - The select element.
	 * @return {void}
	 */
	announceChange(selectElement) {
		const selectedOption = selectElement.options[selectElement.selectedIndex];
		const selectedText = selectedOption.textContent;

		// Create or update announcement element
		let announcer = this.$element[0].querySelector(
			'.hm-dropdown-renderer__announcer'
		);

		if (!announcer) {
			announcer = document.createElement('div');
			announcer.className = 'hm-dropdown-renderer__announcer';
			announcer.setAttribute('role', 'status');
			announcer.setAttribute('aria-live', 'polite');
			announcer.setAttribute('aria-atomic', 'true');
			announcer.style.position = 'absolute';
			announcer.style.left = '-10000px';
			announcer.style.width = '1px';
			announcer.style.height = '1px';
			announcer.style.overflow = 'hidden';
			this.$element[0].appendChild(announcer);
		}

		// Update announcement text
		announcer.textContent = `Now showing ${selectedText}`;
	}
}

// =============================================================================
// REGISTRATION
// =============================================================================

/**
 * Register the handler with Elementor's frontend.
 *
 * This ensures the handler runs both on the frontend and in the editor preview.
 */
jQuery(window).on('elementor/frontend/init', () => {
	elementorFrontend.elementsHandler.attachHandler(
		'hm_dropdown_renderer',
		HoffmasterDropdownRendererHandler
	);
});
